import Input from '@/shared/components/rebranded/Input/Input';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import nb from 'date-fns/locale/nb';
import parseISO from 'date-fns/parseISO';
import toDate from 'date-fns/toDate';
import { useCallback, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PopoverContainer } from '../../../pages/claimForms/components/PopoverContainer';
import { useMedia, usePrevious } from '../../../shared/hooks';
import { FieldProps, StringSchema } from '../../types';
import { joinErrors } from '../../validation/utils';
import { hasErrors } from '../../validation/validateSchema';
import { HourPicker } from '../HourPicker';
import '../react-datepicker.overrides.css';
import styles from './DateField.module.css';
import { HourPickerWrapper } from './styled';

const LANG_NO = 'nb';
const AUTO_COMPLETE = 'off';

export const DATE_FORMATS = ['dd.MM.yyyy', 'dd.MM.yy', 'd.M.yyyy', 'd.M.yy'];
registerLocale(LANG_NO, nb);

const maxDate = (schema: StringSchema) =>
  schema.format === 'date-time-not-future' ||
  schema.format === 'date-not-future'
    ? new Date()
    : undefined;

// date-fns 2.9.0 formatISO has timezone information which we don't want
export const formatDateTime = (date: Date) =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss");

export const convertToDate = (value: string | null | void): Date | null => {
  if (value == null || value === '') {
    return null;
  }

  const dateValue = parseISO(value);
  return toDate(dateValue);
};

const resolveDateFormat = (schema: StringSchema) => {
  if (
    schema.format === 'date-time' ||
    schema.format === 'date-time-not-future'
  ) {
    return {
      formatSchema: (date: Date) => formatDateTime(date),
      hasTime: true
    };
  }

  return {
    formatSchema: (date: Date) => formatISO(date, { representation: 'date' }),
    hasTime: false
  };
};

type DateValue = string | null; // format: yyyy-MM-dd

type DateFieldProps = FieldProps<StringSchema, DateValue>;

export const DateField = (props: DateFieldProps) => {
  const isMobile = useMedia('(max-width: 599px)');

  const { getText } = useTranslations();

  const { hasTime, formatSchema } = resolveDateFormat(props.schema);

  const {
    id,
    schema,
    name,
    value,
    onChange,
    onBlur,
    touched,
    forceShowError,
    errors,
    readOnly
  } = props;

  const prevSchema = usePrevious(schema);

  const handleChange = useCallback(
    (date: Date | null) => {
      onChange({
        target: {
          name,
          value: date == null ? '' : formatSchema(date)
        }
      });
    },
    [formatSchema, name, onChange]
  );

  const handleBlur = () => {
    onBlur({
      target: {
        name,
        touched: true
      }
    });
  };

  const showError = !!((touched || forceShowError) && hasErrors(errors));

  const error = showError ? joinErrors(errors, getText) : undefined;

  const dateValue = convertToDate(value);

  // If we have a new date format, we need to update the "persisted" value in the schema
  useEffect(() => {
    if (prevSchema?.format !== schema.format) {
      handleChange(dateValue);
    }
  }, [dateValue, handleChange, prevSchema, schema]);

  return (
    <>
      <DatePicker
        id={id}
        locale={LANG_NO}
        autoComplete={AUTO_COMPLETE}
        dateFormat={DATE_FORMATS}
        placeholderText="DD.MM.YYYY"
        calendarClassName={styles.calendar}
        maxDate={maxDate(schema)}
        selected={dateValue}
        onChange={handleChange}
        onBlur={handleBlur}
        customInput={
          <Input
            id={`${id}_input`}
            name={`${id}_input`}
            inputProps={{
              className: styles.innerInput
            }}
            label={schema.title}
            hint={schema.$uiOptions?.hint}
            labelPopover={
              schema.description ? (
                <PopoverContainer
                  id={props.id}
                  description={schema.description}
                />
              ) : undefined
            }
            medium
            error={error}
            onChange={() => {}}
            value=""
          />
        }
        disabled={readOnly}
        withPortal={isMobile}
      />

      <HourPickerWrapper>
        {hasTime && (
          <HourPicker
            id={`${id}_time`}
            name={name}
            label="Når på døgnet skjedde det?"
            hint="Hvis du er usikker, velger du så nøyaktig du klarer."
            onChange={handleChange}
            value={dateValue}
            error={error}
            disabled={readOnly}
          />
        )}
      </HourPickerWrapper>
    </>
  );
};
